import { useState } from 'react'
import {
  analisarSistemaVM,
  composicaoDoSistema,
  EFICIENCIA_POR_ROLDANA_PADRAO,
  parametrosDoObjeto,
  RAZOES_VANTAGEM,
  type ObjetoTabletop,
} from '../../dominio/tabletop'

const virgula = (valor: number, casas = 1) => valor.toFixed(casas).replace('.', ',')

/**
 * Inspetor do sistema de vantagem mecânica: quanto se puxa para levantar uma
 * carga, com e sem o atrito das roldanas, e a tabela de todas as razões. O
 * sistema se desenha na cena com as setas de força de cada trecho e a seta
 * grande de puxada na ponta livre.
 */
export function InspetorVantagem({ objeto }: { objeto: ObjetoTabletop }) {
  const medidas = parametrosDoObjeto(objeto)
  const composicao = composicaoDoSistema(medidas.razao!)
  const [cargaKgf, setCargaKgf] = useState(100)
  const [eficiencia, setEficiencia] = useState(EFICIENCIA_POR_ROLDANA_PADRAO)
  const r = analisarSistemaVM({ razao: composicao.razao, cargaKgf, eficienciaPorRoldana: eficiencia })

  return (
    <fieldset className="inspetor-vitima inspetor-corda">
      <legend>Redução de força</legend>
      <p className="corda-resumo">
        <strong>{composicao.nome}</strong>
        <span className="dica">
          {' '}
          · {composicao.roldanasMoveis} roldana{composicao.roldanasMoveis > 1 ? 's' : ''} móvel{composicao.roldanasMoveis > 1 ? 'is' : ''} e {composicao.roldanasFixas} fixa{composicao.roldanasFixas > 1 ? 's' : ''}
        </span>
      </p>
      <label className="campo">
        <span>Carga a levantar (kgf)</span>
        <input
          type="number"
          aria-label="Carga a levantar (kgf)"
          min={1}
          max={1000}
          step={5}
          value={cargaKgf}
          onChange={(e) => {
            const numero = Number(e.target.value)
            if (e.target.value !== '' && Number.isFinite(numero)) setCargaKgf(Math.min(1000, Math.max(1, numero)))
          }}
        />
      </label>
      <label className="campo">
        <span>Eficiência de cada roldana (%)</span>
        <input
          type="number"
          aria-label="Eficiência de cada roldana (%)"
          min={50}
          max={100}
          step={1}
          value={Math.round(eficiencia * 100)}
          onChange={(e) => {
            const numero = Number(e.target.value)
            if (e.target.value !== '' && Number.isFinite(numero)) setEficiencia(Math.min(100, Math.max(50, numero)) / 100)
          }}
        />
      </label>
      <ul className="lista-pontos-ancoragem" aria-live="polite">
        <li>
          Sem atrito: {composicao.razao}:1, puxa-se {virgula(r.forcaIdealKgf)} kgf
        </li>
        <li>
          Com atrito: vantagem real {virgula(r.vantagemReal, 2)}:1, puxa-se {virgula(r.forcaRealKgf)} kgf (
          {virgula(r.acrescimoPeloAtrito * 100, 0)}% a mais)
        </li>
        <li>Para a carga subir 1 m, a corda anda {r.cordaPorMetroDeCarga} m</li>
        <li>A ancoragem do bloco fixo segura cerca de {virgula(r.cargaNaAncoragemKgf, 0)} kgf</li>
      </ul>
      <table className="tabela-pernas">
        <caption className="dica">Todas as razões, para {cargaKgf} kgf</caption>
        <thead>
          <tr>
            <th scope="col">Razão</th>
            <th scope="col">Sem atrito</th>
            <th scope="col">Com atrito</th>
          </tr>
        </thead>
        <tbody>
          {RAZOES_VANTAGEM.map((razao) => {
            const linha = analisarSistemaVM({ razao, cargaKgf, eficienciaPorRoldana: eficiencia })
            return (
              <tr key={razao} aria-current={razao === composicao.razao ? 'true' : undefined}>
                <th scope="row">{razao}:1</th>
                <td>{virgula(linha.forcaIdealKgf)} kgf</td>
                <td>{virgula(linha.forcaRealKgf)} kgf</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="dica">
        Na cena: o olhal da esquerda é o do bloco fixo (prenda à ancoragem), o da direita é o do bloco móvel (liga à carga) e a seta
        amarela grande mostra para onde se puxa. As setas pequenas em cada trecho mostram a força, que puxa a carga para o bloco fixo.
        Valores didáticos: o atrito real depende da roldana, do diâmetro e da corda.
      </p>
    </fieldset>
  )
}
